import { useToast } from '@chakra-ui/react';
import {
	useMutation,
	UseMutationResult,
	useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { useProductPolling } from '../../../../pages/admin/cadastros/produto/hooks/useProductPolling';
import {
	ProductItem,
	UploadRequest,
} from '../../../../pages/admin/cadastros/produto/types/product.types';
import { processPayload } from '../../../../pages/admin/cadastros/produto/utils/productProcessing';
import {
	getPollingConfig,
	POLLING_CONFIG,
	QUERY_KEYS,
	TOAST_MESSAGES,
} from '../../../../pages/admin/cadastros/shared/constants/polling.constants';
import {
	cleanupBulkCreateLogStorage,
	createBulkCreateErrorLog,
	createBulkCreateSuccessLog,
} from '../../../../utils/logging/bulkCreateLogger';
import { uploadProducts } from '../../../api/requests/uploads/uploads';

export const useUploadProducts = (
	setSuccessUpload,
	openTableModal,
	isOpen,
	onClose
) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [previousData, setPreviousData] = useState<ProductItem[] | null>(
		null
	);

	const showSuccessToast = useCallback(async () => {
		await createBulkCreateSuccessLog('/bulkCreate/product');
		cleanupBulkCreateLogStorage('/bulkCreate/product');

		toast({
			title: TOAST_MESSAGES.SUCCESS.title,
			description: TOAST_MESSAGES.SUCCESS.description,
			position: 'bottom-left',
			status: 'success',
			duration: POLLING_CONFIG.TOAST_DURATION,
			isClosable: true,
		});
	}, [toast]);

	const showProcessingCompleteToast = useCallback(() => {
		toast({
			title: TOAST_MESSAGES.PROCESSING_COMPLETE.title,
			description: TOAST_MESSAGES.PROCESSING_COMPLETE.description,
			position: 'bottom-left',
			status: 'success',
			duration: POLLING_CONFIG.TOAST_DURATION,
			isClosable: true,
		});
	}, [toast]);

	const { getCurrentTableData, startDataChangeDetection } =
		useProductPolling();

	const mutation = useMutation(
		(data: unknown) => {
			return uploadProducts(data);
		},
		{
			onSuccess: async (
				success: { quantidade_erros?: number; arquivo_tag?: string },
				variables: unknown
			) => {
				if (
					success?.quantidade_erros !== undefined &&
					success.quantidade_erros > 0
				) {
					await createBulkCreateErrorLog('/bulkCreate/product', {
						quantidade_erros: success.quantidade_erros,
						errors: '[]',
					});
					cleanupBulkCreateLogStorage('/bulkCreate/product');

					openTableModal();
					setSuccessUpload(false);
					if (!isOpen) {
						toast({
							title: TOAST_MESSAGES.UPLOAD_ERROR.title,
							description:
								TOAST_MESSAGES.UPLOAD_ERROR.description,
							position: 'bottom-left',
							status: 'error',
							duration: POLLING_CONFIG.ERROR_TOAST_DURATION,
							isClosable: true,
						});
					}
				} else {
					const variablesTyped = variables as UploadRequest;
					const isUpdate = variablesTyped?.atualizar === true;

					if (isUpdate) {
						const checkAndStartPolling = () => {
							const blocksProcessing = localStorage.getItem(
								'product-upload-blocks-processing'
							);
							const lastBlockTag = localStorage.getItem(
								'product-upload-last-block-tag'
							);
							const pollingStarted = localStorage.getItem(
								'product-upload-polling-started'
							);
							if (
								!blocksProcessing &&
								!pollingStarted &&
								lastBlockTag &&
								success?.arquivo_tag === lastBlockTag
							) {
								localStorage.setItem(
									'product-upload-polling-started',
									'true'
								);

								const fileLinesStr = localStorage.getItem(
									'product-upload-file-lines'
								);
								const fileLines = fileLinesStr
									? Number.parseInt(fileLinesStr, 10)
									: null;
								const pollingConfig = fileLines
									? getPollingConfig(fileLines)
									: POLLING_CONFIG;

								if (fileLinesStr) {
									localStorage.removeItem(
										'product-upload-file-lines'
									);
								}

								const processedPayload =
									processPayload(variablesTyped);
								setPreviousData(getCurrentTableData());
								startDataChangeDetection(
									processedPayload,
									previousData,
									showSuccessToast,
									showProcessingCompleteToast,
									() => {},
									pollingConfig
								);
							} else if (blocksProcessing || !lastBlockTag) {
								setTimeout(checkAndStartPolling, 500);
							}
						};
						setTimeout(checkAndStartPolling, 100);
					} else {
						await createBulkCreateSuccessLog('/bulkCreate/product');
						cleanupBulkCreateLogStorage('/bulkCreate/product');
						localStorage.removeItem('product-upload-processing');

						globalThis.dispatchEvent(
							new Event('product-upload-processing-changed')
						);
						queryClient.invalidateQueries({
							queryKey: QUERY_KEYS.PRODUTO_LIST,
						});
					}

					setSuccessUpload(true);
					onClose();
				}
			},
			onError: async () => {
				await createBulkCreateErrorLog('/bulkCreate/product', {
					message: 'Erro desconhecido ao fazer upload do arquivo',
				});
				cleanupBulkCreateLogStorage('/bulkCreate/product');

				onClose();
				toast({
					title: TOAST_MESSAGES.UNKNOWN_ERROR.title,
					description: TOAST_MESSAGES.UNKNOWN_ERROR.description,
					position: 'bottom-left',
					status: 'error',
					duration: POLLING_CONFIG.ERROR_TOAST_DURATION,
					isClosable: true,
				});
				setSuccessUpload(false);
			},
		}
	);

	return mutation as unknown as UseMutationResult<
		unknown,
		unknown,
		void,
		unknown
	>;
};
